// useUploadQueue.ts
import { useState } from "react";
import { Storage } from "aws-amplify";

export type UploadStatus = "pending" | "uploading" | "success" | "failed";
export interface UploadTarget {
  id: string;
  key: string;
  file: File;
  metadata: Record<string, string>;
}
export interface ItemState {
  status: UploadStatus;
  progress: number;
  error?: string;
}

export function useUploadQueue(concurrency = 5) {
  const [states, setStates] = useState<Record<string, ItemState>>({});
  const [isUploading, setIsUploading] = useState(false);

  const patch = (id: string, p: Partial<ItemState>) =>
    setStates((prev) => ({ ...prev, [id]: { ...(prev[id] ?? { status: "pending", progress: 0 }), ...p } }));

  const uploadOne = async (t: UploadTarget): Promise<boolean> => {
    patch(t.id, { status: "uploading", progress: 0, error: undefined });
    try {
      await Storage.put(t.key, t.file, {
        level: "public",
        contentType: t.file.type,
        metadata: t.metadata,
        progressCallback: (pr: { loaded: number; total: number }) =>
          patch(t.id, { progress: pr.total ? Math.round((pr.loaded / pr.total) * 100) : 0 }),
      });
      patch(t.id, { status: "success", progress: 100 });
      return true;
    } catch (err: any) {
      patch(t.id, { status: "failed", error: err?.message ?? "อัปโหลดไม่สำเร็จ" });
      return false;
    }
  };

  // Fixed-size worker pool draining a shared index.
  const start = async (targets: UploadTarget[]): Promise<string[]> => {
    setIsUploading(true);
    setStates((prev) => {
      const next = { ...prev };
      targets.forEach((t) => (next[t.id] = { status: "pending", progress: 0 }));
      return next;
    });
    const succeeded: string[] = [];
    let cursor = 0;
    const worker = async () => {
      while (cursor < targets.length) {
        const t = targets[cursor++];
        // eslint-disable-next-line no-await-in-loop
        if (await uploadOne(t)) succeeded.push(t.id);
      }
    };
    await Promise.all(Array.from({ length: Math.min(concurrency, targets.length) }, worker));
    setIsUploading(false);
    return succeeded;
  };

  const retry = async (t: UploadTarget): Promise<void> => {
    setIsUploading(true);
    await uploadOne(t);
    setIsUploading(false);
  };

  const reset = () => setStates({});

  return { states, isUploading, start, retry, reset };
}
